import { Fragment } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { parseChatError } from "../../../shared/chatErrors";
import { CONTEXT_LIMIT_MESSAGE } from "../../../shared/contextLimit";
import { type StreamMessage } from "../../hooks/useStreamingText";
import { formatHoverTime } from "../../lib/formatTimestamp";
import { userDisplayName } from "../../lib/userDisplayName";
import { parseApprovePlanInput } from "../../tools/ApprovePlan";
import { Avatar, getUserColor } from "../Avatar";
import { ServiceAccountAvatar } from "../ServiceAccountAvatar";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { type ChatTool } from "./ChatTool";
import { ErrorMessage } from "./ErrorMessage";
import { CompactionEntry } from "./CompactionEntry";
import { ToolUse } from "./ToolUse";
import { TypewriterText } from "./TypewriterText";

import {
  GitSyncMessage,
  isPushMessage,
  MergeMessage,
  PushMessage,
  systemMessageIcon,
} from "./SystemMessages";
import { UserMessageBubble } from "./UserMessageBubble";

import type { GroupedEntry } from "./Transcript";
import {
  Message,
  UserMessageTimestamp,
  UserMessageWrapper,
} from "./transcriptStyles";

type Props = {
  entry: ChatEntryWithSubTables;
  nextGrouped: GroupedEntry | undefined;
  dateMarker: React.ReactNode;
  isGroupChat: boolean;
  currentUserId: string | undefined;
  expandedToolUse: string | null;
  setExpandedToolUse: React.Dispatch<React.SetStateAction<string | null>>;
  toolsByName: Map<string, ChatTool>;
  streamingEntries: ReadonlyMap<string, StreamMessage>;
  isCurrentUserChat: boolean;
  canWrite: boolean;
  onProvideToolResult: (
    entryId: string,
    output: unknown,
    isError: boolean,
    options?: { pauseAfter?: boolean }
  ) => void;
  onRetryFromError: (errorEntryId: string) => void;
  onTypewriterAnimatingChange: (animating: boolean) => void;
  resolveFileHref?: (href: string) => string | null;
};

export function MessageRow({
  entry,
  nextGrouped,
  dateMarker,
  isGroupChat,
  currentUserId,
  expandedToolUse,
  setExpandedToolUse,
  toolsByName,
  streamingEntries,
  isCurrentUserChat,
  canWrite,
  onProvideToolResult,
  onRetryFromError,
  onTypewriterAnimatingChange,
  resolveFileHref,
}: Props) {
  "use memo";
  const nextEntry =
    nextGrouped?.type === "single" ? nextGrouped.entry : undefined;
  const isUser = entry.entryType === "UserMessage";
  const roleChange =
    nextEntry &&
    (entry.entryType === "UserMessage") !==
      (nextEntry.entryType === "UserMessage");
  const typeChange = nextEntry && entry.entryType !== nextEntry.entryType;
  const nextIsToolGroup = nextGrouped?.type === "toolGroup";

  return (
    <Fragment>
      {dateMarker}
      <Message
        style={{
          ...(isUser && {
            maxWidth: "85%",
            alignSelf: "flex-end",
            alignItems: "flex-end",
            marginBottom: 32,
          }),
          ...(roleChange || (isUser && nextIsToolGroup)
            ? { marginBottom: 32 }
            : nextIsToolGroup && entry.entryType === "SystemMessage"
              ? { marginBottom: 2 }
              : (typeChange || nextIsToolGroup) && {
                  marginBottom: 12,
                }),
        }}
      >
        {entry.entryType === "UserMessage" &&
          entry.userMessage &&
          (() => {
            const messageUser = entry.userMessage.user;
            const messageServiceAccount = entry.userMessage.serviceAccount;
            const author = messageUser ?? messageServiceAccount;
            const userColor =
              isGroupChat && author ? getUserColor(author.id) : null;

            return (
              <UserMessageWrapper data-user-message-id={entry.id}>
                <UserMessageBubble
                  text={entry.userMessage.text ?? ""}
                  isDictated={!!entry.userMessage.isDictated}
                  attachments={entry.attachments}
                  userColor={userColor}
                />
                {isGroupChat && author && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: -6,
                      right: -6,
                    }}
                  >
                    <Tooltip
                      content={
                        messageUser
                          ? messageUser.id === currentUserId
                            ? "You"
                            : userDisplayName(messageUser)
                          : messageServiceAccount!.name
                      }
                    >
                      {messageUser ? (
                        <Avatar
                          user={messageUser}
                          size={12}
                          tooltip={false}
                          bare={false}
                        />
                      ) : (
                        <ServiceAccountAvatar
                          seed={messageServiceAccount!.id}
                          size={12}
                        />
                      )}
                    </Tooltip>
                  </div>
                )}
                <UserMessageTimestamp>
                  {formatHoverTime(entry.createdAt)}
                </UserMessageTimestamp>
              </UserMessageWrapper>
            );
          })()}

        {entry.entryType === "AssistantMessage" &&
          entry.assistantMessage &&
          (() => {
            const text =
              streamingEntries.get(entry.id)?.text ??
              entry.assistantMessage.text;
            const isStreaming =
              streamingEntries.get(entry.id)?.status === "streaming";
            const errorDetail = isStreaming ? null : parseChatError(text);
            if (errorDetail != null) {
              return (
                <ErrorMessage
                  title={errorDetail.title}
                  message={errorDetail.message}
                  createdAt={entry.createdAt}
                  canRetry={isCurrentUserChat && canWrite}
                  onRetry={() => onRetryFromError(entry.id)}
                  suggestModelSwitch={
                    isCurrentUserChat && canWrite && errorDetail.isModelError
                  }
                />
              );
            }
            return (
              <TypewriterText
                text={text}
                streaming={isStreaming}
                onAnimatingChange={onTypewriterAnimatingChange}
                resolveFileHref={resolveFileHref}
              />
            );
          })()}

        {entry.entryType === "Compaction" && entry.compaction && (
          <CompactionEntry />
        )}

        {entry.entryType === "AssistantToolUse" &&
          entry.toolCall?.toolName === "approvePlan" &&
          (() => {
            const plan = parseApprovePlanInput(entry.toolCall.input);
            if (!plan) return null;
            return (
              <TypewriterText
                text={`**${plan.title}**\n\n${plan.plan}`}
                streaming={false}
              />
            );
          })()}

        {entry.entryType === "SystemMessage" &&
          entry.systemMessage &&
          (entry.systemMessage.text.startsWith("Merged") ? (
            <MergeMessage text={entry.systemMessage.text} />
          ) : entry.systemMessage.text.startsWith("Synced from git") ? (
            <GitSyncMessage text={entry.systemMessage.text} />
          ) : isPushMessage(entry.systemMessage.text) ? (
            <PushMessage text={entry.systemMessage.text} />
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                height: 28,
                gap: 4,
                padding: "6px 10px 6px 8px",
                borderRadius: 8,
                width: "fit-content",
                ...(entry.systemMessage.text.startsWith("Saved:") && {
                  marginTop: 16,
                }),
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <Icon
                  name={systemMessageIcon(entry.systemMessage.text)}
                  size={12}
                  color="var(--text-secondary)"
                />
                <Text variant="text-small" color="var(--text-secondary)">
                  {entry.systemMessage.text === CONTEXT_LIMIT_MESSAGE
                    ? "Reached the context limit"
                    : entry.systemMessage.text}
                </Text>
              </div>
            </div>
          ))}

        {entry.entryType === "AssistantToolUse" &&
          entry.toolCall &&
          entry.toolCall.toolName !== "approvePlan" && (
            <ToolUse
              toolUse={{
                id: entry.id,
                toolName: entry.toolCall.toolName,
                input: entry.toolCall.input,
                output: entry.toolCall.output,
                isError: entry.toolCall.isError,
              }}
              result={
                entry.toolCall.completedAt != null
                  ? {
                      id: entry.id,
                      output: entry.toolCall.output,
                      isError: entry.toolCall.isError ?? false,
                    }
                  : null
              }
              expanded={expandedToolUse === entry.id}
              toggleExpanded={() =>
                setExpandedToolUse((was) =>
                  was === entry.id ? null : entry.id
                )
              }
              toolAfter={nextEntry?.entryType === "AssistantToolUse"}
              tool={toolsByName.get(entry.toolCall.toolName)}
              createdAt={entry.createdAt}
              completedAt={entry.toolCall.completedAt}
              onProvideResult={onProvideToolResult}
            />
          )}

        {/* UserAction entries are now grouped via userActionGroup */}
      </Message>
    </Fragment>
  );
}
